import {
  calculateMatchPoints,
  noveltyFactor,
  opponentId,
  validateMinimumSubstance
} from './heuristics.js';
import {
  leaderboardLabel,
  leaderboardMatchesCertificate
} from './leaderboardBuckets.js';
import {
  displayRating,
  replayCertificateRatings
} from './ratings.js';

export function weekStart(timestamp = Date.now()) {
  const date = new Date(timestamp);
  const day = date.getUTCDay();
  const daysSinceMonday = (day + 6) % 7;
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() - daysSinceMonday
  );
}

export function mergeShardCertificates(shardSets) {
  const merged = new Map();
  const values = shardSets instanceof Map
    ? [...shardSets.values()]
    : Object.values(shardSets);
  values.flat().forEach((certificate) => {
    if (certificate?.certificateHash) {
      merged.set(certificate.certificateHash, certificate);
    }
  });
  return [...merged.values()].sort(
    (left, right) =>
      left.endTs - right.endTs
      || left.certificateHash.localeCompare(right.certificateHash)
  );
}

function proofDuration(certificate) {
  if (!certificate.rated) {
    return 0;
  }
  const fromProofs = Math.min(
    ...certificate.players.map((player) => {
      const proof = certificate.vdfProofs?.[player.playerId];
      return proof
        ? (proof.totalSteps / Math.max(1, proof.calibration?.stepsPerSecond || 1)) * 1000
        : 0;
    })
  );
  // Dual-signed wall-clock span is a floor: end-of-game lightweight VDF proofs
  // (1-step) would otherwise always fail the 20% base-time substance check.
  const wallMs = Math.max(0, (certificate.endTs || 0) - (certificate.startTs || 0));
  return Math.max(fromProofs || 0, wallMs);
}

function scoringDedupeKey(certificate) {
  return certificate.gameId || certificate.gameHash || certificate.certificateHash;
}

export function buildLeaderboard(
  certificates,
  {
    mode = 'standard',
    timeClass = 'blitz',
    variant = 'standard',
    period = 'weekly',
    now = Date.now(),
    subscribedShards = [],
    totalShardCount = 256
  } = {}
) {
  const ordered = [...certificates]
    .filter(
      (certificate) =>
        certificate.endTs <= now
        && leaderboardMatchesCertificate(certificate, { mode, timeClass, variant })
    )
    .sort(
      (left, right) =>
        left.endTs - right.endTs
        || left.certificateHash.localeCompare(right.certificateHash)
    );
  // Peers may store the same finished game under different certificate hashes
  // (local finalize vs gossiped copy). Score each game once.
  const seenGames = new Set();
  const unique = [];
  for (const certificate of ordered) {
    const key = scoringDedupeKey(certificate);
    if (!key || seenGames.has(key)) {
      continue;
    }
    seenGames.add(key);
    unique.push(certificate);
  }
  const ratings = replayCertificateRatings(unique);
  const history = [];
  const scores = new Map();
  const periodStart = period === 'weekly' ? weekStart(now) : -Infinity;

  for (const certificate of unique) {
    const substance = validateMinimumSubstance(certificate, {
      verifiedDurationMs: proofDuration(certificate)
    });
    for (const player of certificate.players) {
      if (certificate.endTs < periodStart) {
        continue;
      }
      const opponent = opponentId(certificate, player.playerId);
      const novelty = noveltyFactor(
        history,
        player.playerId,
        opponent,
        certificate.endTs
      );
      const pointResult = calculateMatchPoints({
        certificate,
        playerId: player.playerId,
        novelty,
        substance
      });
      // Duplicate / unrated certs stay off the board. Short rated games still
      // list players (0 points) so a first verified result is visible.
      if (!certificate.rated || pointResult.reason === 'duplicate-transcript') {
        continue;
      }
      if (!player?.playerId) {
        continue;
      }
      const current = scores.get(player.playerId) || {
        playerId: player.playerId,
        publicKey: player.publicKey,
        points: 0,
        games: 0,
        wins: 0,
        draws: 0,
        losses: 0
      };
      if (pointResult.eligible) {
        current.points += pointResult.points;
      }
      current.games += 1;
      const isWhite = certificate.players[0].playerId === player.playerId;
      const won = certificate.result === (isWhite ? '1-0' : '0-1');
      const draw = certificate.result === '½-½';
      current.wins += won ? 1 : 0;
      current.draws += draw ? 1 : 0;
      current.losses += !won && !draw ? 1 : 0;
      scores.set(player.playerId, current);
    }
    history.push(certificate);
  }

  const entries = [...scores.values()]
    .map((entry) => {
      const ratingBucket = mode === 'standard'
        ? timeClass
        : `${variant}:${timeClass}`;
      return {
        ...entry,
        points: Math.round(entry.points * 100) / 100,
        rating: displayRating(ratings[entry.playerId]?.[ratingBucket])
      };
    })
    .sort(
      (left, right) =>
        right.points - left.points
        || right.rating - left.rating
        || left.playerId.localeCompare(right.playerId)
    )
    .map((entry, index) => ({ rank: index + 1, ...entry }));

  return {
    mode,
    timeClass,
    variant: mode === 'variant' ? variant : 'standard',
    label: leaderboardLabel({ mode, timeClass, variant }),
    period,
    generatedAt: now,
    periodStart: Number.isFinite(periodStart) ? periodStart : null,
    entries,
    certificateCount: ordered.length,
    coverage: {
      subscribedShards: [...new Set(subscribedShards)].sort(),
      covered: new Set(subscribedShards).size,
      total: totalShardCount
    }
  };
}
